"use client";

import { useState } from 'react';
import AnimatedDropZone from '@/components/AnimatedDropZone';
import FeedbackChat from '@/components/FeedbackChat';
import LimitReachedDialog from '@/components/LimitReachedDialog';
import PayloadErrorDialog from '@/components/PayloadErrorDialog';
import { useTheme } from '@/contexts/ThemeContext';
import { getDefaultConfig, getDecryptedSettings } from '@/lib/crypto';
import { RESUME_STORAGE_KEY, JD_STORAGE_KEY, ANALYSIS_RESULT_KEY } from '@/stores/resumeStore';
import { useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
export default function Dashboard() {
  const {
    theme
  } = useTheme();
  const {
    user
  } = useAuth();
  const isDark = theme === 'dark';
  const [file, setFile] = useState<File | null>(null);
  const [jobDescription, setJobDescription] = useState<string>('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [synonymInput, setSynonymInput] = useState<string>('');
  const [showSynonymModal, setShowSynonymModal] = useState(false);
  const [showFeedbackChat, setShowFeedbackChat] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [showRawJson, setShowRawJson] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [showPayloadErrorDialog, setShowPayloadErrorDialog] = useState(false);

  // Restore previous analysis result if available
  useEffect(() => {
    const storedResult = localStorage.getItem(ANALYSIS_RESULT_KEY);
    if (storedResult) {
      try {
        const parsedResult = JSON.parse(storedResult);
        setResult(parsedResult);
        setSessionId(parsedResult.session_id || '');

        // Also restore JD purely for UI consistency if user wants to see what they entered
        const storedJD = localStorage.getItem(JD_STORAGE_KEY);
        if (storedJD) {
          setJobDescription(storedJD);
        }
      } catch (e) {
        console.error("Failed to restore analysis result", e);
      }
    }
  }, []);
  const handleUpload = async (uploadedFile: File) => {
    setFile(uploadedFile);
    setLoading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', uploadedFile);
    if (jobDescription.trim()) {
      formData.append('job_description', jobDescription);
    }

    // Add LLM Config (Default Provider)
    const config = getDefaultConfig();
    if (config) {
      formData.append('llm_config', JSON.stringify(config));
    }
    try {
      const headers: HeadersInit = {};
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('http://localhost:8000/analyze', {
        method: 'POST',
        headers: headers,
        body: formData
      });
      if (response.status === 429) {
        setShowLimitDialog(true);
        setLoading(false);
        return;
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Check if it's our payload limit error
        if (response.status === 400 && errorData.detail && errorData.detail.includes("too long")) {
          setShowPayloadErrorDialog(true);
          setLoading(false);
          return;
        }
        throw new Error(errorData.detail || 'Analysis failed');
      }
      const data = await response.json();
      console.log('Full API Response:', data);
      setResult(data);
      setSessionId(data.session_id || '');

      // Save extracted data for editor
      // Save extracted data for editor
      if (data.extracted_data) {
        localStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(data.extracted_data));
        localStorage.setItem(ANALYSIS_RESULT_KEY, JSON.stringify(data));
        if (jobDescription) {
          localStorage.setItem(JD_STORAGE_KEY, jobDescription);
        }
      }
    } catch (err) {
      setError("Failed to process resume. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  const getScoreColor = (percentage: number) => {
    if (percentage >= 85) return isDark ? 'text-green-400' : 'text-green-600';
    if (percentage >= 70) return isDark ? 'text-yellow-400' : 'text-yellow-600';
    if (percentage >= 55) return isDark ? 'text-orange-400' : 'text-orange-600';
    return isDark ? 'text-red-400' : 'text-red-600';
  };
  const getScoreBgColor = (percentage_0: number) => {
    if (percentage_0 >= 85) return 'bg-green-500';
    if (percentage_0 >= 70) return 'bg-yellow-500';
    if (percentage_0 >= 55) return 'bg-orange-500';
    return 'bg-red-500';
  };
  const getGradeColor = (grade: string) => {
    const colors: Record<string, string> = {
      'A': isDark ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-green-100 text-green-700 border-green-300',
      'B': isDark ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' : 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'C': isDark ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' : 'bg-orange-100 text-orange-700 border-orange-300',
      'D': isDark ? 'bg-red-500/20 text-red-300 border-red-500/30' : 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[grade] || colors['D'];
  };

  // Helper to render issues/suggestions lists
  const renderList = (items: string[] | undefined, type: 'issue' | 'suggestion') => {
    if (!items || items.length === 0) return null;
    return <ul className="space-y-1.5 mt-2">
                {items.map((item, i) => <li key={i} className="text-sm flex gap-2 items-start">
                        <span className={type === 'issue' ? 'text-red-500' : 'text-green-500'}>
                            {type === 'issue' ? '•' : '✓'}
                        </span>
                        <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{item}</span>
                    </li>)}
            </ul>;
  };
  return <div className="min-h-screen pt-20 md:pt-32 pb-24 md:pb-40 px-3 md:px-8 max-w-7xl mx-auto">
            {/* Header */}
            {!result && <div className="text-center mb-12 mt-8 md:mt-0 animate-float">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-blue-500">
                        Resume Health Check
                    </h1>
                    <p className={`max-w-2xl mx-auto text-lg ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        Get a detailed diagnosis of your resume's ATS compatibility and actionable steps to improve it.
                    </p>
                </div>}

            {!result ? <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Job Description Input */}
                    <div className={`glass-panel p-6 transition-colors ${isDark ? 'hover:border-purple-500/30' : 'hover:border-purple-300 bg-white/80 border-slate-200 shadow-sm'}`}>
                        <label className={`flex items-center gap-2 mb-4 text-sm font-semibold ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>
                            <span className="text-xl">📝</span> Target Job Description
                        </label>
                        <textarea value={jobDescription} onChange={e_0 => setJobDescription(e_0.target.value)} placeholder="Paste the job description here (JD)...&#10;&#10;Our AI checks your resume against specific keywords and requirements found in the JD." className={`w-full h-64 border rounded-xl p-4 placeholder-slate-400 focus:outline-none resize-none text-sm transition-all ${isDark ? 'bg-white/5 border-white/10 text-white focus:border-purple-500/50 focus:bg-white/10' : 'bg-white border-slate-200 text-slate-900 focus:border-purple-400 focus:bg-slate-50'}`} />
                    </div>

                    {/* Upload Zone */}
                    <div className="flex flex-col gap-4">
                        <AnimatedDropZone onFileSelect={handleUpload} isUploading={loading} />

                        {error && <div className={`p-4 rounded-xl text-sm flex items-center gap-2 animate-bounce-slow ${isDark ? 'bg-red-500/10 border border-red-500/20 text-red-200' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                                <span>⚠️</span> {error}
                            </div>}

                        <div className={`glass-panel p-4 flex items-center gap-3 opacity-80 ${isDark ? '' : 'bg-white/80 border-slate-200'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>💡</div>
                            <p className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                                Pro Tip: Uploading with a JD increases your score accuracy by 40%.
                            </p>
                        </div>
                    </div>
                </div> : <div className="space-y-8 animate-slide-up">
                    {/* Results Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b border-white/10 pb-6">
                        <div>
                            <div className="text-sm text-slate-400 mb-1">Analysis Report for</div>
                            <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{file?.name || 'Resume.pdf'}</h2>
                            <div className="flex gap-2 mt-2 flex-wrap">
                                <span className={`text-xs px-2 py-1 rounded-full ${result.score?.has_job_description ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
                                    {result.score?.has_job_description ? '✓ JD Provided' : '⚠ No JD'}
                                </span>
                                {result.score?.jd_keywords_found > 0 && <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-300">
                                        {result.score.jd_keywords_found} JD keywords extracted
                                    </span>}
                            </div>
                        </div>
                        <div className="flex gap-3 flex-wrap">
                            <button onClick={() => setShowRawJson(!showRawJson)} className={`px-4 py-2 border rounded-lg text-xs font-medium transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10 border-white/10 text-slate-300' : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-600'}`}>
                                {showRawJson ? 'Hide' : 'Show'} Raw Data
                            </button>
                            <button onClick={() => setShowFeedbackChat(true)} className="px-6 py-2.5 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 rounded-lg font-semibold transition-all shadow-lg shadow-purple-500/20 flex items-center gap-2">
                                <span>💬</span> Ask AI Coach
                            </button>
                            <button onClick={() => {
            setResult(null);
            setJobDescription('');
            setSessionId('');
            setShowRawJson(false);
            localStorage.removeItem(RESUME_STORAGE_KEY);
            localStorage.removeItem(JD_STORAGE_KEY);
            localStorage.removeItem(ANALYSIS_RESULT_KEY);
          }} className={`px-6 py-2.5 border rounded-lg font-semibold transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20 border-white/20 text-white' : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700'}`}>
                                New Scan
                            </button>
                        </div>
                    </div>

                    {/* Raw JSON Debug Panel */}
                    {showRawJson && <div className={`glass-panel p-4 overflow-auto max-h-96 relative group/json ${isDark ? '' : 'bg-slate-50 border-slate-200'}`}>
                            <button onClick={() => {
          navigator.clipboard.writeText(JSON.stringify(result, null, 2));
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        }} className={`absolute bottom-4 right-4 p-2 rounded-lg transition-all opacity-0 group-hover/json:opacity-100 flex items-center gap-2 text-xs font-medium shadow-lg backdrop-blur-sm z-10 ${isCopied ? 'bg-green-500/20 text-green-400 border border-green-500/30' : isDark ? 'bg-white/10 hover:bg-white/20 text-slate-300 border border-white/10' : 'bg-white hover:bg-slate-100 text-slate-600 border border-slate-200'}`} title="Copy JSON">
                                {isCopied ? <>
                                        <span>✓</span> Copied!
                                    </> : <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                        </svg>
                                        Copy
                                    </>}
                            </button>
                            <pre className={`text-xs whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                {JSON.stringify(result, null, 2)}
                            </pre>
                        </div>}

                    {/* ==================== SCORE OVERVIEW ==================== */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        {/* Main Score Card */}
                        <div className={`glass-panel p-8 flex flex-col items-center justify-center relative overflow-hidden group lg:col-span-1 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-blue-500/5 group-hover:from-purple-500/10 group-hover:to-blue-500/10 transition-colors"></div>

                            <div className="relative w-40 h-40 flex items-center justify-center mb-4">
                                <svg className="w-full h-full transform -rotate-90 drop-shadow-2xl" viewBox="0 0 192 192">
                                    <circle cx="96" cy="96" r="80" stroke="currentColor" strokeWidth="12" fill="transparent" className={isDark ? "text-slate-800" : "text-slate-200"} />
                                    <circle cx="96" cy="96" r="80" stroke="currentColor" strokeWidth="12" fill="transparent" strokeLinecap="round" strokeDasharray={502} strokeDashoffset={502 - 502 * (result.score?.total_score || 0) / 100} className={`${getScoreColor(result.score?.percentage || 0)} transition-all duration-1000 ease-out`} />
                                </svg>
                                <div className="absolute text-center flex flex-col items-center">
                                    <span className={`text-5xl font-bold tracking-tighter ${isDark ? 'text-white' : 'text-slate-900'}`}>{result.score?.total_score}</span>
                                    <span className={`px-3 py-0.5 rounded-full text-xs font-bold mt-1 uppercase tracking-wide border ${getGradeColor(result.score?.grade)}`}>
                                        Grade {result.score?.grade}
                                    </span>
                                </div>
                            </div>
                            <div className="text-center text-sm text-slate-400">
                                {result.score?.percentage}% ATS Compatible
                            </div>
                        </div>

                        {/* Score Breakdown Cards */}
                        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                            {/* Contact Info Score */}
                            <div className={`glass-card p-4 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-slate-400">Contact</span>
                                    <span className={`text-lg font-bold ${getScoreColor((result.score?.breakdown?.contact_info?.score / result.score?.breakdown?.contact_info?.max_score || 0) * 100)}`}>
                                        {result.score?.breakdown?.contact_info?.score}/{result.score?.breakdown?.contact_info?.max_score}
                                    </span>
                                </div>
                                <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                    <div className={`h-full rounded-full ${getScoreBgColor((result.score?.breakdown?.contact_info?.score / result.score?.breakdown?.contact_info?.max_score || 0) * 100)}`} style={{
                width: `${(result.score?.breakdown?.contact_info?.score / result.score?.breakdown?.contact_info?.max_score || 0) * 100}%`
              }}></div>
                                </div>
                            </div>

                            {/* Sections Score */}
                            <div className={`glass-card p-4 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-slate-400">Sections</span>
                                    <span className={`text-lg font-bold ${getScoreColor((result.score?.breakdown?.sections?.score / result.score?.breakdown?.sections?.max_score || 0) * 100)}`}>
                                        {result.score?.breakdown?.sections?.score}/{result.score?.breakdown?.sections?.max_score}
                                    </span>
                                </div>
                                <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                    <div className={`h-full rounded-full ${getScoreBgColor((result.score?.breakdown?.sections?.score / result.score?.breakdown?.sections?.max_score || 0) * 100)}`} style={{
                width: `${(result.score?.breakdown?.sections?.score / result.score?.breakdown?.sections?.max_score || 0) * 100}%`
              }}></div>
                                </div>
                            </div>

                            {/* Keywords Score */}
                            <div className={`glass-card p-4 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-slate-400">Keywords</span>
                                    <span className={`text-lg font-bold ${getScoreColor((result.score?.breakdown?.keywords?.score / result.score?.breakdown?.keywords?.max_score || 0) * 100)}`}>
                                        {result.score?.breakdown?.keywords?.score}/{result.score?.breakdown?.keywords?.max_score}
                                    </span>
                                </div>
                                <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                    <div className={`h-full rounded-full ${getScoreBgColor((result.score?.breakdown?.keywords?.score / result.score?.breakdown?.keywords?.max_score || 0) * 100)}`} style={{
                width: `${(result.score?.breakdown?.keywords?.score / result.score?.breakdown?.keywords?.max_score || 0) * 100}%`
              }}></div>
                                </div>
                            </div>

                            {/* Formatting Score */}
                            <div className={`glass-card p-4 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm text-slate-400">Format</span>
                                    <span className={`text-lg font-bold ${getScoreColor((result.score?.breakdown?.formatting?.score / result.score?.breakdown?.formatting?.max_score || 0) * 100)}`}>
                                        {result.score?.breakdown?.formatting?.score}/{result.score?.breakdown?.formatting?.max_score}
                                    </span>
                                </div>
                                <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                    <div className={`h-full rounded-full ${getScoreBgColor((result.score?.breakdown?.formatting?.score / result.score?.breakdown?.formatting?.max_score || 0) * 100)}`} style={{
                width: `${(result.score?.breakdown?.formatting?.score / result.score?.breakdown?.formatting?.max_score || 0) * 100}%`
              }}></div>
                                </div>
                                <div className="text-xs text-slate-500 mt-1">{result.score?.breakdown?.formatting?.word_count} words</div>
                            </div>
                        </div>
                    </div>

                    {/* ==================== OVERALL FEEDBACK ==================== */}
                    {result.score?.overall_feedback && result.score.overall_feedback.length > 0 && <div className={`glass-panel p-6 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <span>📋</span> Overall Assessment
                            </h3>
                            <ul className="space-y-3">
                                {result.score.overall_feedback.map((fb: string, i_0: number) => <li key={i_0} className={`text-sm flex gap-2 items-start p-3 rounded-lg ${isDark ? 'text-slate-300 bg-white/5' : 'text-slate-700 bg-slate-100'}`}>
                                        <span className="shrink-0">{fb.startsWith('✅') || fb.startsWith('⚠️') || fb.startsWith('❌') || fb.startsWith('🔴') || fb.startsWith('📌') ? '' : '→'}</span>
                                        <span>{fb}</span>
                                    </li>)}
                            </ul>
                        </div>}

                    {/* ==================== AI SUMMARY CRITIQUE ==================== */}
                    {result.feedback?.summary_critique && <div className="glass-panel p-6 bg-gradient-to-br from-purple-500/5 to-blue-500/5">
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <span>🤖</span> AI Summary Critique
                            </h3>
                            <p className={`leading-relaxed italic ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                "{result.feedback.summary_critique}"
                            </p>
                        </div>}

                    {/* ==================== TOP 3 PRIORITIES ==================== */}
                    {result.feedback?.top_3_priorities && result.feedback.top_3_priorities.length > 0 && <div className={`glass-panel p-6 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <span>🚨</span> Top Priority Fixes
                            </h3>
                            <div className="space-y-3">
                                {result.feedback.top_3_priorities.map((priority: string, i_1: number) => <div key={i_1} className={`flex gap-4 items-start p-4 rounded-xl border ${isDark ? 'bg-red-500/5 border-red-500/10' : 'bg-red-50 border-red-100'}`}>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold shrink-0 ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>
                                            {i_1 + 1}
                                        </div>
                                        <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{priority}</p>
                                    </div>)}
                            </div>
                        </div>}

                    {/* ==================== CONTACT INFO DETAILS ==================== */}
                    <div className={`glass-panel p-6 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <span>👤</span> Contact Information
                            <span className={`text-sm font-normal px-2 py-0.5 rounded ${getGradeColor(result.score?.breakdown?.contact_info?.score >= 12 ? 'A' : result.score?.breakdown?.contact_info?.score >= 8 ? 'B' : 'C')}`}>
                                {result.score?.breakdown?.contact_info?.score}/{result.score?.breakdown?.contact_info?.max_score}
                            </span>
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            {result.score?.breakdown?.contact_info?.items && Object.entries(result.score.breakdown.contact_info.items).map(([key, value]: [string, any]) => <div key={key} className={`p-3 rounded-lg text-center ${value.found ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                                    <div className="text-lg mb-1">{value.found ? '✅' : '❌'}</div>
                                    <div className="text-xs capitalize text-slate-400">{key}</div>
                                    {value.value && <div className="text-xs text-slate-500 truncate mt-1">{value.value}</div>}
                                </div>)}
                        </div>
                        {/* Contact Feedback */}
                        {result.score?.breakdown?.contact_info?.feedback && <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                {result.score.breakdown.contact_info.feedback.missing?.length > 0 && <div className="bg-red-500/5 p-4 rounded-lg border border-red-500/10">
                                        <h4 className="text-sm font-semibold text-red-300 mb-2">Missing</h4>
                                        {renderList(result.score.breakdown.contact_info.feedback.missing, 'issue')}
                                    </div>}
                                {result.score.breakdown.contact_info.feedback.suggestions?.length > 0 && <div className="bg-green-500/5 p-4 rounded-lg border border-green-500/10">
                                        <h4 className="text-sm font-semibold text-green-300 mb-2">Suggestions</h4>
                                        {renderList(result.score.breakdown.contact_info.feedback.suggestions, 'suggestion')}
                                    </div>}
                            </div>}
                        {/* LLM Contact Feedback */}
                        {result.feedback?.contact_section && <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                {result.feedback.contact_section.issues?.length > 0 && <div className="bg-red-500/5 p-4 rounded-lg border border-red-500/10">
                                        <h4 className="text-sm font-semibold text-red-300 mb-2">🤖 AI Issues</h4>
                                        {renderList(result.feedback.contact_section.issues, 'issue')}
                                    </div>}
                                {result.feedback.contact_section.suggestions?.length > 0 && <div className="bg-green-500/5 p-4 rounded-lg border border-green-500/10">
                                        <h4 className="text-sm font-semibold text-green-300 mb-2">🤖 AI Suggestions</h4>
                                        {renderList(result.feedback.contact_section.suggestions, 'suggestion')}
                                    </div>}
                            </div>}
                    </div>

                    {/* ==================== SECTION-BY-SECTION ANALYSIS ==================== */}
                    <h3 className="text-2xl font-bold mt-8 mb-4 flex items-center gap-2">
                        <span>📊</span> Section-by-Section Analysis
                    </h3>

                    <div className="space-y-6">
                        {result.score?.breakdown?.sections?.sections && Object.entries(result.score.breakdown.sections.sections).map(([sectionName, sectionData]: [string, any]) => {
          // Get LLM feedback for this section
          const llmSection = result.feedback?.[`${sectionName.toLowerCase()}_section`];
          return <div key={sectionName} className={`glass-panel p-6 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
                                        <div className="flex items-center gap-3">
                                            <h4 className={`text-xl font-bold capitalize ${isDark ? 'text-white' : 'text-slate-900'}`}>{sectionName}</h4>
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${sectionData.present ? isDark ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700' : isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'}`}>
                                                {sectionData.present ? 'Found' : 'Missing'}
                                            </span>
                                            <span className={`px-2 py-0.5 rounded text-xs ${sectionData.weight === 'critical' ? isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700' : sectionData.weight === 'important' ? isDark ? 'bg-yellow-500/20 text-yellow-300' : 'bg-yellow-100 text-yellow-800' : isDark ? 'bg-slate-500/20 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                                                {sectionData.weight}
                                            </span>
                                        </div>
                                        <div className={`flex items-center gap-3 px-4 py-2 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
                                            <span className={`text-2xl font-bold ${getScoreColor(sectionData.score / sectionData.max_score * 100)}`}>
                                                {sectionData.score}/{sectionData.max_score}
                                            </span>
                                            <div className={`w-16 h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                                                <div className={`h-full rounded-full ${getScoreBgColor(sectionData.score / sectionData.max_score * 100)}`} style={{
                    width: `${sectionData.score / sectionData.max_score * 100}%`
                  }}></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Quality Notes */}
                                    {sectionData.quality_notes?.length > 0 && <div className="mb-4 flex flex-wrap gap-2">
                                            {sectionData.quality_notes.map((note: string, i_2: number) => <span key={i_2} className={`text-xs px-2 py-1 rounded-full border ${isDark ? 'bg-blue-500/10 text-blue-300 border-blue-500/20' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>{note}</span>)}
                                        </div>}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Issues from Scoring */}
                                        {sectionData.issues?.length > 0 && <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/10">
                                                <h5 className="font-semibold text-red-300 mb-2 flex items-center gap-2">
                                                    <span>⚠️</span> Issues Found
                                                </h5>
                                                {renderList(sectionData.issues, 'issue')}
                                            </div>}

                                        {/* Suggestions from Scoring */}
                                        {sectionData.suggestions?.length > 0 && <div className="bg-green-500/5 rounded-xl p-4 border border-green-500/10">
                                                <h5 className="font-semibold text-green-300 mb-2 flex items-center gap-2">
                                                    <span>💡</span> Suggestions
                                                </h5>
                                                {renderList(sectionData.suggestions, 'suggestion')}
                                            </div>}

                                        {/* LLM Issues */}
                                        {llmSection?.issues?.length > 0 && <div className="bg-red-500/5 rounded-xl p-4 border border-red-500/10">
                                                <h5 className="font-semibold text-red-300 mb-2 flex items-center gap-2">
                                                    <span>🤖</span> AI Issues
                                                </h5>
                                                {renderList(llmSection.issues, 'issue')}
                                            </div>}

                                        {/* LLM Suggestions */}
                                        {llmSection?.suggestions?.length > 0 && <div className="bg-green-500/5 rounded-xl p-4 border border-green-500/10">
                                                <h5 className="font-semibold text-green-300 mb-2 flex items-center gap-2">
                                                    <span>🤖</span> AI Suggestions
                                                </h5>
                                                {renderList(llmSection.suggestions, 'suggestion')}
                                            </div>}

                                        {/* LLM Missing Skills (for Skills section) */}
                                        {llmSection?.missing_skills?.length > 0 && <div className="bg-orange-500/5 rounded-xl p-4 border border-orange-500/10 md:col-span-2">
                                                <h5 className="font-semibold text-orange-300 mb-2 flex items-center gap-2">
                                                    <span>🔍</span> Missing Skills (AI)
                                                </h5>
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {llmSection.missing_skills.map((skill: string, i_3: number) => <span key={i_3} className={`px-2 py-1 border text-xs rounded-full ${isDark ? 'bg-orange-500/10 border-orange-500/20 text-orange-300' : 'bg-orange-50 border-orange-200 text-orange-800'}`}>{skill}</span>)}
                                                </div>
                                            </div>}
                                    </div>
                                </div>;
        })}
                    </div>

                    {/* ==================== KEYWORDS ANALYSIS ==================== */}
                    <div className={`glass-panel p-6 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <span>🔑</span> Keyword Analysis
                            <span className={`text-sm font-normal px-2 py-0.5 rounded ${getGradeColor(result.score?.breakdown?.keywords?.match_percentage >= 70 ? 'A' : result.score?.breakdown?.keywords?.match_percentage >= 50 ? 'B' : 'C')}`}>
                                {result.score?.breakdown?.keywords?.match_percentage || 0}% Match
                            </span>
                        </h3>

                        {result.score?.breakdown?.keywords?.warning && <div className={`mb-4 p-3 rounded-lg text-sm border ${isDark ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-300' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
                                ⚠️ {result.score.breakdown.keywords.warning}
                            </div>}

                        {result.score?.breakdown?.keywords?.note && <div className={`mb-4 p-3 rounded-lg text-sm border ${isDark ? 'bg-blue-500/10 border-blue-500/20 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                                💡 {result.score.breakdown.keywords.note}
                            </div>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Found Keywords */}
                            <div>
                                <h4 className="text-sm font-semibold text-green-400 uppercase tracking-wide mb-3">
                                    ✓ Found Keywords ({result.score?.breakdown?.keywords?.found_keywords?.length || 0})
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {result.score?.breakdown?.keywords?.found_keywords?.length > 0 ? result.score.breakdown.keywords.found_keywords.map((kw: string, i_4: number) => <span key={i_4} className={`px-3 py-1 border text-xs rounded-full ${isDark ? 'bg-green-500/10 border-green-500/20 text-green-300' : 'bg-green-50 border-green-200 text-green-700'}`}>{kw}</span>) : <span className="text-sm text-slate-500 italic">No keywords matched</span>}
                                </div>
                            </div>

                            {/* Partial Matches */}
                            {result.score?.breakdown?.keywords?.partial_matches?.length > 0 && <div>
                                    <h4 className="text-sm font-semibold text-blue-400 uppercase tracking-wide mb-3">
                                        ~ Partial Matches ({result.score.breakdown.keywords.partial_matches.length})
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {result.score.breakdown.keywords.partial_matches.map((match: any, i_5: number) => <span key={i_5} className={`px-3 py-1 border text-xs rounded-full ${isDark ? 'bg-blue-500/10 border-blue-500/20 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                                                {match.keyword} → {match.found_as}
                                            </span>)}
                                    </div>
                                </div>}

                            {/* Missing Keywords */}
                            <div className="md:col-span-2">
                                <h4 className="text-sm font-semibold text-red-400 uppercase tracking-wide mb-3">
                                    ✗ Missing Keywords ({result.score?.breakdown?.keywords?.missing_keywords?.length || 0})
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {result.score?.breakdown?.keywords?.missing_keywords?.length > 0 ? result.score.breakdown.keywords.missing_keywords.map((kw_0: string, i_6: number) => <span key={i_6} className={`px-3 py-1 border text-xs rounded-full cursor-pointer transition-colors ${isDark ? 'bg-red-500/10 border-red-500/20 text-red-300 hover:bg-red-500/20' : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'}`}>{kw_0}</span>) : <span className="text-sm text-slate-500 italic">All required keywords found!</span>}
                                </div>
                                {result.score?.breakdown?.keywords?.missing_keywords?.length > 0 && <button onClick={() => setShowSynonymModal(true)} className="mt-4 text-xs text-purple-400 hover:text-purple-300 underline underline-offset-4">
                                        Report synonyms / alternative terms you used
                                    </button>}
                            </div>
                        </div>

                        {/* Keyword Optimization Suggestions from LLM */}
                        {result.feedback?.keyword_optimization?.length > 0 && <div className="mt-6 bg-purple-500/5 p-4 rounded-lg border border-purple-500/10">
                                <h4 className="text-sm font-semibold text-purple-300 mb-2">🤖 AI Keyword Suggestions</h4>
                                {renderList(result.feedback.keyword_optimization, 'suggestion')}
                            </div>}
                    </div>

                    {/* ==================== FORMATTING ANALYSIS ==================== */}
                    <div className={`glass-panel p-6 ${isDark ? '' : 'bg-white border-slate-200'}`}>
                        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <span>📄</span> Formatting & Length
                            <span className={`text-sm font-normal px-2 py-0.5 rounded ${getGradeColor(result.score?.breakdown?.formatting?.score >= 12 ? 'A' : result.score?.breakdown?.formatting?.score >= 8 ? 'B' : 'C')}`}>
                                {result.score?.breakdown?.formatting?.score}/{result.score?.breakdown?.formatting?.max_score}
                            </span>
                        </h3>
                        <div className="flex items-center gap-4 mb-4">
                            <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{result.score?.breakdown?.formatting?.word_count}</div>
                            <div className="text-slate-400 text-sm">words<br /><span className="text-xs text-slate-500">Target: 300-800</span></div>
                        </div>
                        {result.score?.breakdown?.formatting?.feedback?.length > 0 && <div className="space-y-2">
                                {result.score.breakdown.formatting.feedback.map((fb_0: string, i_7: number) => <div key={i_7} className={`text-sm p-2 rounded ${fb_0.startsWith('✓') ? isDark ? 'bg-green-500/10 text-green-300' : 'bg-green-50 text-green-700' : fb_0.startsWith('✗') ? isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-700' : isDark ? 'bg-slate-500/10 text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                                        {fb_0}
                                    </div>)}
                            </div>}
                        {/* LLM Formatting Issues */}
                        {result.feedback?.formatting_issues?.length > 0 && <div className="mt-4 bg-orange-500/5 p-4 rounded-lg border border-orange-500/10">
                                <h4 className="text-sm font-semibold text-orange-300 mb-2">🤖 AI Formatting Issues</h4>
                                {renderList(result.feedback.formatting_issues, 'issue')}
                            </div>}
                    </div>

                    {/* ==================== CRITICAL MISSING SECTIONS ==================== */}
                    {result.score?.breakdown?.sections?.critical_missing?.length > 0 && <div className="glass-panel p-6 bg-red-500/5 border border-red-500/20">
                            <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-red-300">
                                <span>🚫</span> Critical Missing Sections
                            </h3>
                            <div className="flex flex-wrap gap-3">
                                {result.score.breakdown.sections.critical_missing.map((section: string, i_8: number) => <span key={i_8} className={`px-4 py-2 border rounded-lg font-medium ${isDark ? 'bg-red-500/20 border-red-500/30 text-red-200' : 'bg-red-100 border-red-200 text-red-800'}`}>{section}</span>)}
                            </div>
                            <p className="text-sm text-red-200/70 mt-3">
                                These sections are essential for ATS systems. Add them immediately to improve your score.
                            </p>
                        </div>}
                </div>}

            {/* ==================== MODALS ==================== */}
            {showSynonymModal && <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="glass-panel max-w-2xl w-full p-8 animate-slide-up">
                        <h3 className="text-xl font-bold mb-4">Add Alternative Keywords</h3>
                        <p className="text-sm text-slate-400 mb-6">
                            Help us understand your resume better by mapping missing keywords to terms you actually used.
                        </p>
                        <textarea value={synonymInput} onChange={e_1 => setSynonymInput(e_1.target.value)} placeholder="Example:&#10;AWS = Amazon Web Services&#10;React = ReactJS" className={`w-full h-40 border rounded-xl p-4 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 resize-none text-sm mb-6 ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`} />
                        <div className="flex gap-4">
                            <button onClick={() => setShowSynonymModal(false)} className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
                                Cancel
                            </button>
                            <button onClick={() => setShowSynonymModal(false)} className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 rounded-xl text-sm font-bold transition-all shadow-lg shadow-purple-500/20">
                                Adjust Score
                            </button>
                        </div>
                    </div>
                </div>}

            {showFeedbackChat && sessionId && <FeedbackChat sessionId={sessionId} onClose={() => setShowFeedbackChat(false)} />}

            {/* Daily Limit Reached Dialog */}
            <LimitReachedDialog isOpen={showLimitDialog} onClose={() => setShowLimitDialog(false)} />

            {/* Payload Error Dialog */}
            <PayloadErrorDialog isOpen={showPayloadErrorDialog} onClose={() => setShowPayloadErrorDialog(false)} />
        </div>;
}